import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import FacebookIcon from "@mui/icons-material/Facebook";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import apiConfig from "../config/api.config";
import "./authPages.css";

const GRAPH_VERSION = "v19.0";
const FACEBOOK_SCOPE = "public_profile,email";
/** Silent hang fallback after FB.login was invoked (popup may stay open a long time). */
const FACEBOOK_LOGIN_HANG_TIMEOUT_MS = 45000;
const FACEBOOK_LOGIN_DIDNT_OPEN_MESSAGE = "Facebook sign-in didn't open. Please try again.";

function apiUrl(path) {
  const base = String(apiConfig.BASE_URL || "").replace(/\/$/, "");
  return `${base}${path}`;
}

function loadFacebookSdk() {
  if (window.FB) return Promise.resolve(window.FB);
  if (window.__fbSdkPromise) return window.__fbSdkPromise;
  window.__fbSdkPromise = new Promise((resolve, reject) => {
    const previous = window.fbAsyncInit;
    window.fbAsyncInit = () => {
      if (typeof previous === "function") previous();
      resolve(window.FB);
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => reject(new Error("Facebook SDK failed to load"));
    document.body.appendChild(script);
  });
  return window.__fbSdkPromise;
}

async function postProvider(path, body) {
  const response = await axios.post(apiUrl(path), body, { withCredentials: true });
  return response.data;
}

/**
 * Google uses the same Sign in with Google button as estore (ID token posted to the server).
 * Facebook uses standard Facebook Login with public_profile and email only.
 * FB SDK + app ID preload on mount so FB.login runs synchronously in the click handler
 * (Chrome blocks the popup if login is deferred past an await).
 */
function OAuthProviderButtons({ onAuthenticated, onError }) {
  const [googleClientId, setGoogleClientId] = useState("");
  const [facebookAppId, setFacebookAppId] = useState("");
  const [facebookReady, setFacebookReady] = useState(false);
  const [facebookBusy, setFacebookBusy] = useState(false);
  const [googleBtnWidth, setGoogleBtnWidth] = useState(320);
  const facebookTimeoutRef = useRef(null);
  const facebookLoginActiveRef = useRef(false);
  const googleWrapRef = useRef(null);

  function clearFacebookTimeout() {
    if (facebookTimeoutRef.current) {
      clearTimeout(facebookTimeoutRef.current);
      facebookTimeoutRef.current = null;
    }
  }

  function resetFacebookBusy() {
    facebookLoginActiveRef.current = false;
    clearFacebookTimeout();
    setFacebookBusy(false);
  }

  useEffect(() => () => clearFacebookTimeout(), []);

  useEffect(() => {
    let cancelled = false;
    axios.get(apiUrl("/auth/google/client-id"))
      .then((response) => {
        if (!cancelled) setGoogleClientId(response.data?.data || "");
      })
      .catch(() => {
        if (!cancelled) setGoogleClientId("");
      });
    axios.get(apiUrl("/auth/facebook/app-id"))
      .then((response) => {
        if (!cancelled) setFacebookAppId(response.data?.data || "");
      })
      .catch(() => {
        if (!cancelled) setFacebookAppId("");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const el = googleWrapRef.current;
    if (!el) return undefined;
    const updateWidth = () => {
      const next = Math.floor(el.clientWidth);
      if (next > 0) setGoogleBtnWidth(Math.min(next, 400));
    };
    updateWidth();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, [googleClientId]);

  useEffect(() => {
    if (!facebookAppId) {
      setFacebookReady(false);
      return undefined;
    }
    let cancelled = false;
    setFacebookReady(false);
    loadFacebookSdk()
      .then((FB) => {
        if (cancelled || !FB?.login) return;
        // fedCM: false forces the classic OAuth popup. When Chrome has FedCM
        // disabled for the site (after a dismissal), FedCM-mode FB.login never
        // calls back and the button hangs — see ORK-173 / Meta FedCM docs.
        FB.init({
          appId: facebookAppId,
          cookie: false,
          xfbml: false,
          version: GRAPH_VERSION,
          fedCM: false,
        });
        if (!cancelled) setFacebookReady(true);
      })
      .catch(() => {
        if (!cancelled) setFacebookReady(false);
      });
    return () => {
      cancelled = true;
    };
  }, [facebookAppId]);

  async function finish(body) {
    if (onAuthenticated) {
      await onAuthenticated(body);
    }
  }

  function report(error, fallback) {
    const code = error?.response?.data?.error;
    const message = typeof code === "string"
      ? "Social sign-in did not complete. Try again, or use your number and password."
      : (error?.message || fallback);
    if (onError) onError(message);
  }

  async function onGoogleSuccess(response) {
    try {
      const body = await postProvider("/auth/google", { credential: response.credential });
      await finish(body);
    } catch (error) {
      report(error, "Google sign-in failed");
    }
  }

  function startFacebookLogin() {
    if (!facebookAppId) {
      if (onError) onError("Facebook sign-in is not configured.");
      return;
    }
    if (!facebookReady || !window.FB?.login) {
      if (onError) onError("Facebook is still loading. Try again.");
      return;
    }

    facebookLoginActiveRef.current = true;
    setFacebookBusy(true);
    clearFacebookTimeout();

    try {
      // Must stay synchronous in the click handler — any await before this loses the user gesture
      // and Chrome blocks the Facebook popup.
      window.FB.login((loginResponse) => {
        const accessToken = loginResponse?.authResponse?.accessToken;
        // Closing the popup (or declining) → cancelled. Do not leave a prior
        // "didn't open" toast (ORK-173 Retest 5/6).
        if (!accessToken) {
          resetFacebookBusy();
          if (onError) onError("Facebook sign-in was cancelled.");
          return;
        }
        // Late success after hang timeout: still complete sign-in.
        facebookLoginActiveRef.current = false;
        clearFacebookTimeout();
        setFacebookBusy(true);
        postProvider("/auth/facebook", { access_token: accessToken })
          .then((body) => finish(body))
          .catch((error) => report(error, "Facebook sign-in failed"))
          .finally(() => resetFacebookBusy());
      }, { scope: FACEBOOK_SCOPE });

      // FB.login started — popup may stay open while the user signs in.
      // Do not show "didn't open" while waiting; only silently unlock after a
      // long hang so the user can retry (ORK-173 Retest 6).
      facebookTimeoutRef.current = setTimeout(() => {
        facebookLoginActiveRef.current = false;
        setFacebookBusy(false);
        facebookTimeoutRef.current = null;
      }, FACEBOOK_LOGIN_HANG_TIMEOUT_MS);
    } catch (error) {
      // FB.login never started — this is the only "didn't open" path.
      resetFacebookBusy();
      if (onError) onError(FACEBOOK_LOGIN_DIDNT_OPEN_MESSAGE);
    }
  }

  const facebookDisabled = !facebookReady || facebookBusy;
  const facebookLabel = facebookBusy
    ? "Connecting…"
    : (!facebookReady && facebookAppId ? "Loading Facebook…" : "Continue with Facebook");

  return (
    <div className="oauth-stack">
      <div className="oauth-divider">
        <span>Or continue with</span>
      </div>
      <div className="oauth-buttons">
        {googleClientId ? (
          <GoogleOAuthProvider clientId={googleClientId}>
            <div className="oauth-google" ref={googleWrapRef}>
              <GoogleLogin
                text="continue_with"
                onSuccess={onGoogleSuccess}
                onError={() => {
                  if (onError) onError("Google sign-in failed");
                }}
                width={String(googleBtnWidth)}
                size="large"
                shape="rectangular"
                logo_alignment="center"
              />
            </div>
          </GoogleOAuthProvider>
        ) : null}
        {facebookAppId ? (
          <button
            type="button"
            className="oauth-btn"
            onClick={startFacebookLogin}
            disabled={facebookDisabled}
            aria-label="Continue with Facebook"
          >
            <FacebookIcon sx={{ fontSize: 22, color: "#1877F2" }} aria-hidden />
            {facebookLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export { OAuthProviderButtons };
